import { VariableType } from 'json-to-graphql-query';

import { Resource } from './resource';

/**
 * Resource obsługi KO
 *
 * @export
 * @class KoResource
 * @extends {Resource}
 */
export class KoResource extends Resource {
  /**
   * Podgląd wniosku KO w HTML
   *
   * @param {{ packId: number; data: any }} variables
   * @return {Promise<string>}
   * @memberof KoResource
   */
  public previewProposal(variables: { packId: number; data: any }): Promise<string> {
    const query = this.prepareOptions({
      query: {
        __variables: { packId: 'Int!', data: 'JSON!' },
        paymentPackPreviewKoProposal: {
          __args: { packId: new VariableType('packId'), data: new VariableType('data') }
        }
      }
    });

    return this.query({ query, variables }, 'paymentPackPreviewKoProposal');
  }

  /**
   * Wysyłka wniosku KO do MDOK
   *
   * @param {{packId: number, data: any}} variables
   * @return {Promise<boolean>}
   * @memberof KoResource
   */
  public sendKoProposalToMdok(variables: { packId: number; data: any }): Promise<boolean> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: { packId: 'Int!', data: 'JSON!' },
        paymentPackSendKoProposalToMdok: {
          __args: { packId: new VariableType('packId'), data: new VariableType('data') }
        }
      }
    });

    return this.mutation({ mutation, variables }, 'paymentPackSendKoProposalToMdok');
  }

  /**
   * Podgląd Raportu KO w HTML
   *
   * @param {{ procedureId: number; date: Date; data: any }} variables
   * @return {Promise<string>}
   * @memberof KoResource
   */
  public previewKoReport(variables: { procedureId: number; date: Date; data: any }): Promise<string> {
    const query = this.prepareOptions({
      query: {
        __variables: { procedureId: 'Int!', date: 'DateScalar!', data: 'JSON!' },
        paymentPackPreviewKoReport: {
          __args: {
            procedureId: new VariableType('procedureId'),
            date: new VariableType('date'),
            data: new VariableType('data')
          }
        }
      }
    });

    return this.query({ query, variables }, 'paymentPackPreviewKoReport');
  }

  /**
   * Wysyła sprawozdanie KO do MDOK
   *
   * @param {{ procedureId: number; date: Date; data: any }} variables
   * @return {Promise<boolean>}
   * @memberof KoResource
   */
  public sendKoReportToMdok(variables: { procedureId: number; date: Date; data: any }): Promise<boolean> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: { procedureId: 'Int!', date: 'DateScalar!', data: 'JSON!' },
        paymentPackKoSendReportToMdok: {
          __args: {
            procedureId: new VariableType('procedureId'),
            date: new VariableType('date'),
            data: new VariableType('data')
          }
        }
      }
    });

    return this.mutation({ mutation, variables }, 'paymentPackKoSendReportToMdok');
  }

  /**
   * Wysyła listę płatności KO do MDOK
   *
   * @param {{packId: number}} variables
   * @return {Promise<boolean>}
   * @memberof KoResource
   */
  public sendKoListToMdok(variables: { packId: number }): Promise<boolean> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: { packId: 'Int!' },
        paymentPackSendKoListToMdok: {
          __args: { packId: new VariableType('packId') }
        }
      }
    });
    return this.mutation({ mutation, variables }, 'paymentPackSendKoListToMdok');
  }

  /**
   * Podgląd listy wypłat KO w HTML
   *
   * @param {{ packId: number;}} variables
   * @return {Promise<string>}
   * @memberof KoResource
   */
  public previewKoList(variables: { packId: number }): Promise<string> {
    const query = this.prepareOptions({
      query: {
        __variables: { packId: 'Int!' },
        paymentPackPreviewKoList: {
          __args: { packId: new VariableType('packId') }
        }
      }
    });

    return this.query({ query, variables }, 'paymentPackPreviewKoList');
  }

  /**
   * Oznaczenie jako środki otrzymane
   *
   * @param {{ packId: number; date: Date }} variables
   * @return {Promise<boolean>}
   * @memberof KoResource
   */
  public markAsReceivedFunds(variables: { packId: number; date: Date }): Promise<boolean> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: { packId: 'Int!', date: 'DateScalar!' },
        paymentPackKoMarkAsReceivedFunds: {
          __args: { packId: new VariableType('packId'), date: new VariableType('date') }
        }
      }
    });
    return this.mutation({ mutation, variables }, 'paymentPackKoMarkAsReceivedFunds');
  }

  /**
   * Oznaczenie wielu paczek płatności jako "środki otrzymane"
   *
   * @param {{ packIds: number[]; date: Date }} variables
   * @return {Promise<boolean>}
   * @memberof KoResource
   */
  public markMultipleAsReceivedFunds(variables: { packIds: number[]; date: Date }): Promise<boolean> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: { packIds: '[Int!]!', date: 'DateScalar!' },
        paymentPackKoMarkMultipleAsReceivedFunds: {
          __args: { packIds: new VariableType('packIds'), date: new VariableType('date') }
        }
      }
    });
    return this.mutation({ mutation, variables }, 'paymentPackKoMarkMultipleAsReceivedFunds');
  }

  /**
   * Oznaczenie jako opłacona
   *
   * @param {{ packId: number; date: Date }} variables
   * @return {Promise<boolean>}
   * @memberof KoResource
   */
  public markAsPaid(variables: { packId: number; date: Date }): Promise<boolean> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: { packId: 'Int!', date: 'DateScalar!' },
        paymentPackKoMarkAsPaid: {
          __args: { packId: new VariableType('packId'), date: new VariableType('date') }
        }
      }
    });
    return this.mutation({ mutation, variables }, 'paymentPackKoMarkAsPaid');
  }

  /**
   * Oznacza wiele paczek płatności jako opłacone
   *
   * @param {{ packIds: number[]; date: Date }} variables
   * @return {Promise<boolean>}
   * @memberof KoResource
   */
  public markMultipleAsPaid(variables: { packIds: number[]; date: Date }): Promise<boolean> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: { packIds: '[Int!]!', date: 'DateScalar!' },
        paymentPackKoMarkMultipleAsPaid: {
          __args: { packIds: new VariableType('packIds'), date: new VariableType('date') }
        }
      }
    });
    return this.mutation({ mutation, variables }, 'paymentPackKoMarkMultipleAsPaid');
  }
}

export const koResource = new KoResource();
